import {
  FULL_LIST_TIMEOUT_MS,
  HttpError,
  withTimeout,
} from '../lib/sync/request-signal';

export const BASE_URL = '/api/kitchen';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface RequestOptions extends RequestInit {
  /** Cut the call off after this long. Default: 30 s (FULL_LIST_TIMEOUT_MS). */
  timeoutMs?: number;
}

/** Same as `request`, but returns the whole envelope (the list also carries `cursor`). */
export async function requestEnvelope<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResponse<T> & { cursor?: string; [key: string]: unknown }> {
  const token = localStorage.getItem('kitchen_jwt');
  const { timeoutMs = FULL_LIST_TIMEOUT_MS, signal, ...init } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((options.headers as Record<string, string>) || {}),
  };

  const timed = withTimeout(timeoutMs, signal ?? undefined);
  try {
    const res = await fetch(`${BASE_URL}${path}`, { ...init, headers, signal: timed.signal });

    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new HttpError(res.status, body.error || `HTTP ${res.status}`);
    }

    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Request failed');
    }

    return json;
  } catch (error) {
    if (timed.timedOut()) throw new Error(`Request timed out after ${timeoutMs} ms`);
    throw error;
  } finally {
    timed.dispose();
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const json = await requestEnvelope<T>(path, options);
  return json.data as T;
}

export async function publicRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  const json: ApiResponse<T> = await res.json();

  if (!json.success) {
    throw new Error(json.error || 'Request failed');
  }

  return json.data as T;
}

